import {useCallback} from "react"
import type {BaseMutationOptions, MutationFunctionOptions} from "@apollo/client"
import {MutationHookOptions, MutationTuple, useMutation} from "@apollo/client"
import API from "@irati/utils/services/API/utils/errorHandler/register"
import {
  ApolloLoad,
  deriveHookNameAndRenameFunctionForDevtools,
  DocumentNode,
  ServiceOptions,
  type PBApolloError
} from "./helpers/hookShared"

type MutationResult<A, B> = Prettify<
  Pick<MutationTuple<A, B>["1"], "loading" | "called"> & {
    /** Data from the request */
    data?: A | undefined | null
    /** An error content key from the failing service */
    error?: PBApolloError
    errorMsg?: string
    /** A pending promise that can be passed to `<Loading />` components or collected in array.
     * Note, for `<Button />` components use `loading` prop instead.
     *
     * @example
     * function MyComp() {
     *   const [mutate, {load, data}] = useMyMutation()
     *
     *   useEffect(() => {
     *     mutate()
     *   },[])
     *
     *   return <Loading load={load}>{data}</Loading>
     * }
     *
     * @example
     * function MyComp() {
     *   const [mutate, {loading}] = useMyMutation()
     *
     *   return <ValidationButton loading={loading} onClick={mutate}>Go!</ValidationButton
     * }
     */
    load: ApolloLoad
  }
>

type UseMutationResult<A, B> = [
  (
    options?: Pick<MutationFunctionOptions<A, B>, "optimisticResponse" | "refetchQueries" | "update" | "variables" | "awaitRefetchQueries">
  ) => Promise<A>,
  MutationResult<A, B>
]

/**
 * Stripped down variables to pass to useMutation
 * More can be added if identified as needed, but minimsing the api surface for now
 */
type UseMutationOptions<A, B> = Prettify<
  Pick<MutationHookOptions<A, B>, "variables" | "update" | "refetchQueries" | "onCompleted" | "optimisticResponse"> & {
    /** A callback function that's called when the mutation encounters one or more errors */
    onError?: (error: string, clientOptions?: BaseMutationOptions) => void
  }
>

/**
 * Helper type extract the values from the various parts of the constructed hook
 * @param A - The type of the original hook, you can use `typeof` to convert JavaScript into the type world
 *
 * @example
 * const useBudgetsUpdateMutation = createUseMutation<void, MutationUpdateBudgetCategoriesArgs>(gql`
 *   mutation UpdateBudgetCategories($input: UpdateBudgetCategoriesInput!) {
 *     updateBudgetCategories(input: $input)
 *   }
 * `)
 *
 * type Extracted = UseMutationExtractor<typeof useBudgetsUpdateMutation>
 *
 * type UseBudgetsUpdate = {
 *   update: (updates: string) => Promise<Extracted["mutationResult"]>
 * }
 */
export type UseMutationExtractor<A extends (x: any) => [any, any]> = {
  /** the arguments passed to the hook */
  hookArgs: A extends (arg: infer Arg) => [any, any] ? Arg : never
  /** the argument passed to the mutate function, which is the first return value from the hook */
  mutationArg: A extends (arg: any) => [(arg: infer Arg) => any, any] ? Arg : never
  /** the return type from the mutate function, which is the first return value from the hook */
  mutationResult: A extends (arg: any) => [(arg: any) => infer Arg, any] ? Awaited<Arg> : never
  /** The second return value containing { loading, error, data, ...etx } */
  apollo: A extends (arg: any) => [any, infer Arg] ? Arg : never
}

/**
 * Create a mutaiton hook for reuse throughout the codebase. This is highly recomended over calling `useMutation` directly.
 *
 * The benifit of using this factory function is to get strongly typed values, and marshal the output into more commonly used formats,
 * not limited to but including a `load` value for passing to `Loading` components
 *
 * For details of the underlying `useMutation` see [apollo client](https://www.apollographql.com/docs/react/data/mutations)
 *
 * @example
 * // create our reusable hook
 * export const useBudgetsMutation = createUseMutation<BudgetCategories, MutationBudgetCategoriesArgs>(
 *   gql`mutation WalletBudgets($userId: String!) {
 *       budgetCategories(userId: $userId) {
 *         categories {
 *           uuid
 *         }
 *       }
 *     }`,
 *   {
 *     errorHandlers: [budgetErrorHandler]
 *   }
 * )
 *
 * // somewhere else
 * const MyComp = () => {
 *   const [updateBudgets, {loading, error, data}] = useBudgetsMutation()
 *
 *   return <button onClick={() => updateBudgets({ variables: "hello" })}>change!</button>
 * }
 */
export function createUseMutation<A, B>(mutation: DocumentNode, {errorHandlers = [], key, ...useMutationOptions}: ServiceOptions = {}) {
  errorHandlers.forEach((handler) => {
    API.add(handler)
  })

  function useHook(options: UseMutationOptions<A, B> = {}): UseMutationResult<A, B> {
    /**
     * Wrap options.update fn to get the nested data by `key` if it was provided
     */
    const wrappedUpdate: typeof options.update = (cache, fetchResult, context) => {
      return options.update?.(cache, {...fetchResult, data: getApolloDataByKey(fetchResult.data, key)}, context)
    }

    const wrappedOptions: any = key
      ? {...options, update: wrappedUpdate, context: useMutationOptions.forceEU ? {reqEUinstance: true} : {}}
      : {...options, context: useMutationOptions.forceEU ? {reqEUinstance: true} : {}}

    const [mutate, apollo] = useMutation<A, B>(mutation, wrappedOptions)

    // Here we are overriding the error type to be a PB Error as we know this will be handled by our client
    const error = apollo.error as PBApolloError | undefined

    const data = getApolloDataByKey(apollo.data, key)

    const wrappedMutate = useCallback(
      async (args?: Parameters<UseMutationResult<A, B>[0]>[0]) => {
        const d = await mutate(args)
        // if there is an error, this would throw, so might as well just expose the data
        const mutationData = key && d.data ? ((d.data as any)[key] as A) : d.data
        if (!mutationData && d.errors?.length === 0) {
          throw new Error(
            "there should be data, you are likely ignoring it in the setup, which was not intended at the time of creating this wrapper"
          )
        }
        return mutationData
      },
      [mutate]
    )

    return [
      wrappedMutate,
      {
        ...apollo,
        data,
        error,
        errorMsg: error?.networkError?.formattedMessage ?? error?.message,
        load: {
          loading: apollo.loading,
          data,
          error,
          __brand: "ApolloLoad"
        }
      }
    ]
  }

  deriveHookNameAndRenameFunctionForDevtools(useHook, "Mutation", mutation)
  return useHook
}

function getApolloDataByKey<A>(data: A, key: string | undefined) {
  // here we assert the key index will be of type `A` - don't assume it should be
  // `keyof A` because we often define types in terms of the nested graphql type, not the root type
  return key && data ? ((data as any)[key] as A) : data
}
